import { FormEvent, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { KeyRound, Mail } from 'lucide-react';
import { useResetPassword } from '@/hooks/mutations/useTeamMutation';

interface ResetPasswordProps {
    onSuccess: (token?: string) => void;
}

export default function ResetPassword({ onSuccess }: ResetPasswordProps) {
    const [email, setEmail] = useState('');
    const [temporaryPassword, setTemporaryPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const { mutate: resetPasswordMutate, isPending } = useResetPassword();

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        resetPasswordMutate(
            {
                email: email.trim(),
                temporaryPassword,
                newPassword,
            },
            {
                onSuccess: (result) => {
                    onSuccess(result.token);
                },
            }
        );
    };

    const canSubmit =
        email.trim().length > 0 &&
        temporaryPassword.trim().length > 0 &&
        newPassword.trim().length >= 4;

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted/20 p-4">
            <Card className="relative w-full max-w-md shadow-sm border-border/60">
                <CardHeader className="space-y-1 pb-2">
                    <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <KeyRound className="h-4 w-4" aria-hidden />
                        </div>
                        <div>
                            <CardTitle className="text-xl">Reset password</CardTitle>
                            <CardDescription className="pt-0.5">
                                Enter your work email, the temporary password from your invite, then choose a new password.
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>

                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="reset-email">Email</Label>
                            <div className="relative">
                                <Mail
                                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none"
                                    aria-hidden
                                />
                                <Input
                                    id="reset-email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="h-11 pl-10"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="temporary-password">Temporary password</Label>
                            <Input
                                id="temporary-password"
                                name="temporaryPassword"
                                type="password"
                                autoComplete="current-password"
                                value={temporaryPassword}
                                onChange={(e) => setTemporaryPassword(e.target.value)}
                                placeholder="Temporary password from email"
                                className="h-11"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="new-password">New password</Label>
                            <Input
                                id="new-password"
                                name="newPassword"
                                type="password"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="At least 9 characters"
                                minLength={4}
                                className="h-11"
                            />
                        </div>

                        <Button type="submit" className="w-full h-11" disabled={!canSubmit || isPending}>
                            {isPending ? 'Updating…' : 'Update password'}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
